import * as originPackageService from "../../../../services/originPackage";
import * as originTransportationService from "../../../../services/originTransportation";
import * as settings from "../../../../services/settings";

import {
  Breadcrumb,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Image,
  Input,
  Row,
  Select,
  Spin,
  Tabs,
  TimePicker,
  Typography,
  Upload,
  message,
  notification,
} from "antd";
import { ImagePreview, UploadButton, createHandlePreview, createHandleUpload } from "../../../../utils/uploadUtils.jsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  validateCreateOriginTransportation,
  validateSubmitCreateOriginTransportation,
} from "../../../../Components/validations/validateCreateOriginTransportation";

import AuthGroup from "../../../../../configs/authgroup";
import BtnHistory from "../../../../Components/model/button/history";
import ContentType from "../../../../../configs/contentType";
import { CreateOriginTransportationPackage } from "../../../../Components/package/originTransportation";
import dayjs from "dayjs";
import permissions from "../../../../../configs/permission";
import { useAccessToken } from "../../../../contexts/AccessTokenContext";
import { usePermission } from "../../../../hooks/usePermission";
import { resolveImageUrl } from "../../../../utils/imageUrl";
import { useTranslation } from "react-i18next";

const TYPE_EDIT = "EDIT";
const TYPE_CREATE = "CREATE";
const PACKAGE_LIST_PATH = "/package/originpackagetransportation";

export const OriginPackageTransportationCreatePage = () => {
  const { accessToken } = useAccessToken();
  const hasInitialized = useRef(false);
  const hasFetchedWareHouse = useRef(false);
  const hasFetchedOriginTransportation = useRef(false);
  const { hasPermission, hasGroup, isSuperuser } = usePermission();
  const location = useLocation();
  const data = location.state;
  const { t } = useTranslation("originpackagetransportation", "buttons", "common");
  const [loading, setLoading] = useState(false);
  const [formOriginTransportation] = Form.useForm();
  const [formOriginTransportationImage] = Form.useForm();
  const [listWareHouseOrigin, setListWareHouseOrigin] = useState([]);
  const [listWareHouseDestination, setListWareHouseDestination] = useState([]);
  const { Title } = Typography;
  const { TextArea } = Input;
  const [isCheckCreateSuccess, setIsCreateSuccess] = useState(false);
  const [api, contextHolder] = notification.useNotification();
  const [stateType, setStateType] = useState(TYPE_CREATE);
  const [originTransportation, setOriginTransportation] = useState();
  const [selectErrorOrigin, setSelectErrorOrigin] = useState(false);
  const [selectErrorDestination, setSelectErrorDestination] = useState(false);
  const [isDisable, setIsDisable] = useState(true);
  const [initialValues, setInitialValues] = useState({
    container: undefined,
    external_container: undefined,
    route: undefined,
    origin_id: undefined,
    destination_id: undefined,
    depart_at_date: null,
    depart_at_time: null,
    full_container_load: false,
    note: undefined,
  });

  const openNotificationWithIcon = useCallback(
    (type, msg, description) => {
      api[type]({ message: msg, description });
    },
    [api]
  );
  const [validateFieldRequired, setValidateFieldRequired] = useState({
    fieldContainer: false,
    fieldOriginId: false,
    fieldDestinationId: false,
    fieldDepartAtDate: false,
    fieldDepartAtTime: false,
  });
  const [fileList, setFileList] = useState(Array(8).fill(null));
  const [messageApi] = message.useMessage();
  const navigation = useNavigate();
  const canViewRestrictedFields = isSuperuser || hasGroup(AuthGroup.OriginWarehouse);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");

  const handleUpload = useCallback(createHandleUpload(messageApi), [messageApi]);

  const handleFileListChange = useCallback((index, newList) => {
    const file = newList[0];
    if (!file) {
      setFileList((prev) => {
        const next = [...prev];
        next[index] = null;
        return next;
      });
      return;
    }
    setFileList((prev) => {
      const isDuplicate = file.name && prev.some((f, i) => f && f.name === file.name && i !== index);
      if (isDuplicate) {
        message.error("ชื่อไฟล์ซ้ำ กรุณาเปลี่ยนชื่อไฟล์");
        return prev;
      }
      const next = [...prev];
      next[index] = file;
      return next;
    });
  }, []);

  const handleFileRemove = useCallback(
    (index) => {
      const canRemove =
        hasPermission(permissions.OriginPackageImage.DELETE) ||
        hasPermission(permissions.OriginPackageImage.ADD) ||
        hasPermission(permissions.OriginPackageImage.CHANGE);
      if (!canRemove) {
        message.warning("You are not allowed to remove this file.");
        return false;
      }
      setFileList((prev) => {
        const next = [...prev];
        next[index] = null;
        return next;
      });
    },
    [hasPermission]
  );

  const handlePreview = useCallback(createHandlePreview(setPreviewImage, setPreviewOpen), []);

  const uploadButton = <UploadButton />;

  const fetchWareHouseData = useCallback(async () => {
    if (hasFetchedWareHouse.current) return;
    hasFetchedWareHouse.current = true;
    setLoading(true);
    try {
      const wareHouseData = await originPackageService.getWareHouses(accessToken);
      const origin = [];
      const destination = [];
      wareHouseData.forEach((value) => {
        if (value.is_origin) {
          origin.push({ value: value.id, label: value.warehouse_code });
        }
        if (value.is_destination) {
          destination.push({ value: value.id, label: value.warehouse_code });
        }
      });
      setListWareHouseDestination(destination);
      setListWareHouseOrigin(origin);
      formOriginTransportation.setFieldValue("destination_id", 3);
    } catch (err) {
      console.error("[OriginPackageTransportationCreatePage] fetchWareHouseData: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      hasFetchedWareHouse.current = false;
    } finally {
      setLoading(false);
    }
  }, [accessToken, formOriginTransportation]);

  const setDetailData = useCallback(
    (detail) => {
      formOriginTransportation.setFieldValue("container", detail.container);
      formOriginTransportation.setFieldValue("external_container", detail.external_container);
      formOriginTransportation.setFieldValue("route", detail.route);

      const origin = detail.app_warehouse_app_origintransportation_origin_idToapp_warehouse;
      if (origin) {
        formOriginTransportation.setFieldValue("origin_id", origin.warehouse_code);
      }
      const destination = detail.app_warehouse_app_origintransportation_destination_idToapp_warehouse;
      if (destination) {
        formOriginTransportation.setFieldValue("destination_id", destination.warehouse_code);
      }

      const departAtDatetime = dayjs(detail.depart_at);
      formOriginTransportation.setFieldValue("depart_at_date", departAtDatetime);
      formOriginTransportation.setFieldValue("depart_at_time", departAtDatetime);
      formOriginTransportation.setFieldValue("full_container_load", detail.full_container_load);
      formOriginTransportation.setFieldValue("transportation_status", detail.transportation_status);
      formOriginTransportation.setFieldValue("note", detail.note);

      const images = detail?.app_origintransportationimage;
      if (Array.isArray(images) && images.length > 0) {
        const imagesByPosition = Array(8).fill(null);
        images.forEach((value) => {
          const match = value.image?.match(/uploads\/(\d+)-origin-origintransportation/);
          const position = match ? parseInt(match[1], 10) : null;
          if (position >= 1 && position <= 8) {
            imagesByPosition[position - 1] = {
              uid: value.image.substring(42, 78),
              name: `image_${value.id}.png`,
              status: "done",
              url: resolveImageUrl(value.image),
              id: value.id,
            };
          }
        });
        setFileList(imagesByPosition);
      }
    },
    [formOriginTransportation]
  );

  const selectOriginPackageRoute = useMemo(
    () => [
      { value: "sea", label: "SEA" },
      { value: "land", label: "LAND (EK)" },
    ],
    []
  );

  const initialItems = useMemo(
    () => [
      {
        label: t("Origin_Package_Transportation_Create.Create_Package.Title_Package"),
        children: (
          <CreateOriginTransportationPackage
            data={data}
            accessToken={accessToken}
            isDisabled={isDisable}
            loading={loading}
            originTransportation={originTransportation}
            t={t}
          />
        ),
        key: "1",
      },
    ],
    [data, accessToken, isDisable, loading, originTransportation, t]
  );

  const fetchOriginTransportationData = useCallback(async () => {
    const id = data?.originTransportationData?.id;
    if (!id) return;
    setLoading(true);
    try {
      const response = await originTransportationService.getOriginTransportationById(accessToken, id);
      if (response != null) {
        setDetailData(response);
        setOriginTransportation(response);
      }
    } catch (err) {
      console.error("[OriginPackageTransportationCreatePage] fetchOriginTransportationData: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
    } finally {
      setLoading(false);
    }
  }, [accessToken, data?.originTransportationData?.id, openNotificationWithIcon, setDetailData]);

  const handleSelectDestinationChange = useCallback(() => {
    setSelectErrorDestination(false);
  }, []);

  const handleSelectOriginChange = useCallback(() => {
    setSelectErrorOrigin(false);
  }, []);

  const onSubmit = async () => {
    const values = formOriginTransportation.getFieldsValue();
    const {
      container,
      route,
      expect_arrive_at,
      actual_arrive_at,
      destination_id,
      origin_id,
      note,
      external_container,
    } = values || {};

    if (!destination_id) {
      setSelectErrorDestination(true);
    } else {
      setSelectErrorDestination(false);
    }
    if (!origin_id) {
      setSelectErrorOrigin(true);
    } else {
      setSelectErrorOrigin(false);
    }

    const departAtDate = values.depart_at_date ? values.depart_at_date.format("YYYY-MM-DD") : null;
    const departAtTime = values.depart_at_time ? values.depart_at_time.format("HH:mm:ss") : null;

    const depart_at = [departAtDate, departAtTime].filter(Boolean).join(" ");
    const full_container_load = Boolean(values.full_container_load);
    const transportation_status = "open";
    const { isValid } = validateSubmitCreateOriginTransportation(values);
    if (isValid) {
      const prepareData = {
        container: container,
        route: route || "",
        depart_at: depart_at,
        destination_id: destination_id,
        origin_id: origin_id,
        expect_arrive_at: expect_arrive_at || "",
        actual_arrive_at: actual_arrive_at,
        transportation_status: transportation_status,
        note: note || "",
        external_container: external_container ?? "",
        full_container_load: full_container_load,
      };

      setLoading(true);
      setIsCreateSuccess(false);
      try {
        if (stateType === TYPE_CREATE) {
          const response = await originTransportationService.create(accessToken, prepareData);

          if (response != null) {
            setLoading(false);
            openNotificationWithIcon(
              settings.NotificationType.SUCCESS,
              "Success.",
              "Create origin transportation success"
            );
            setIsCreateSuccess(true);
            setDetailData(response);
            setIsDisable(false);
            setOriginTransportation(response);
          }
        } else {
          if (typeof destination_id === "string") {
            prepareData.destination_id =
              originTransportation.app_warehouse_app_origintransportation_destination_idToapp_warehouse.id;
          }
          if (typeof origin_id === "string") {
            prepareData.origin_id =
              originTransportation.app_warehouse_app_origintransportation_origin_idToapp_warehouse.id;
          }
          prepareData.id = originTransportation.id;
          const response = await originTransportationService.update(accessToken, prepareData);
          if (response != null) {
            setLoading(false);
            openNotificationWithIcon(
              settings.NotificationType.SUCCESS,
              "Success.",
              "Update origin transportation success"
            );
          }
          setDetailData(response);
          setIsDisable(false);
          setOriginTransportation(response);
        }
      } catch (err) {
        console.error("[OriginPackageTransportationCreatePage] onSubmit: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
        setLoading(false);
      }
    } else {
      setIsCreateSuccess(false);
    }
  };
  const onSubmitImages = async () => {
    if (!originTransportation?.id) {
      openNotificationWithIcon(
        settings.NotificationType.WARNING,
        "Warning",
        "Please save transportation data first before uploading images."
      );
      return;
    }

    const formData = new FormData();
    formData.append("origin_transportation_id", originTransportation.id);
    // image
    const old_images = [];
    const file_positions = [];
    fileList.forEach((file, index) => {
      if (file?.id != null) {
        old_images.push(file.id);
      } else if (file?.originFileObj) {
        formData.append("file", file.originFileObj);
        file_positions.push(index + 1);
      }
    });
    formData.append("file_positions", JSON.stringify(file_positions));
    formData.append("old_images", JSON.stringify(old_images));

    setLoading(true);
    try {
      if (stateType === TYPE_CREATE) {
        const response = await originTransportationService.createImages(accessToken, formData);
        if (response != null) {
          setLoading(false);
          openNotificationWithIcon(
            settings.NotificationType.SUCCESS,
            "Success.",
            "Create origin transportation Images success"
          );
          setIsCreateSuccess(true);
          setDetailData(response);
          setIsDisable(false);
          setOriginTransportation(response);
        }
      } else {
        const response = await originTransportationService.updateImages(accessToken, formData);
        if (response != null) {
          setLoading(false);
          openNotificationWithIcon(
            settings.NotificationType.SUCCESS,
            "Success.",
            "Update origin transportation Image success"
          );
          setDetailData(response);
          setIsDisable(false);
          setOriginTransportation((prev) => ({
            ...prev,
            ...response,
            id: prev?.id || response?.id,
          }));
        }
      }
    } catch (err) {
      console.error("[OriginPackageTransportationCreatePage] onSubmitImages: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
    } finally {
      setLoading(false);
    }
  };

  const validateDataOriginTransportationRequired = useCallback(
    async (name, value) => {
      await validateCreateOriginTransportation(validateFieldRequired, setValidateFieldRequired, name, value);
    },
    [validateFieldRequired]
  );

  const breadcrumbItems = useMemo(
    () => [
      { title: <span>{t("Page_Body_Tab_Menu.Home", { ns: "menus" })}</span> },
      { title: <span>{t("Page_Body_Tab_Menu.Menu", { ns: "menus" })}</span> },
      { title: <a href={PACKAGE_LIST_PATH}>{t("Menu_Package.Title_Package", { ns: "menus" })}</a> },
      {
        title: (
          <span style={{ fontWeight: 600, color: "black" }}>
            {stateType === TYPE_EDIT
              ? t("Origin_Package_Transportation_Create.Title_Origin_Package_Transportation_Edit")
              : t("Origin_Package_Transportation_Create.Title_Origin_Package_Transportation_Create")}
          </span>
        ),
      },
    ],
    [stateType, t]
  );

  const setData = useCallback(
    (payload) => {
      setInitialValues({
        container: payload.container ?? undefined,
        external_container: payload.external_container ?? undefined,
        route: payload.route ?? undefined,
        origin_id: payload.app_warehouse_app_origintransportation_origin_idToapp_warehouse?.warehouse_code ?? undefined,
        destination_id:
          payload.app_warehouse_app_origintransportation_destination_idToapp_warehouse?.warehouse_code ?? undefined,
        depart_at_date: payload.depart_at ? dayjs(payload.depart_at) : null,
        depart_at_time: payload.depart_at ? dayjs(payload.depart_at) : null,
        full_container_load: payload.full_container_load ?? false,
        transportation_status: payload.transportation_status ?? undefined,
        note: payload.note ?? undefined,
      });
      setDetailData(payload);
    },
    [setDetailData]
  );

  useEffect(() => {
    if (!accessToken || hasInitialized.current) return;
    hasInitialized.current = true;
    setLoading(true);
    fetchWareHouseData();
    if (data?.type === "EDIT") {
      if (data.originPackageTransportation) {
        setData(data.originPackageTransportation);
      }
      setStateType(TYPE_EDIT);
      setOriginTransportation(data.originTransportationData);
      setIsDisable(false);
      if (!hasFetchedOriginTransportation.current) {
        hasFetchedOriginTransportation.current = true;
        fetchOriginTransportationData();
      }
    }
  }, [
    accessToken,
    data?.type,
    data?.originPackageTransportation,
    data?.originTransportationData,
    fetchWareHouseData,
    setData,
    fetchOriginTransportationData,
  ]);

  return (
    <>
      {contextHolder}
      <Spin spinning={loading}>
        <Row>
          <Col span={24}>
            <Breadcrumb items={breadcrumbItems} />
            <div className="flex justify-between mt-6 mb-4">
              <Title level={2}>
                {data?.type === "EDIT"
                  ? t("Origin_Package_Transportation_Create.Title_Origin_Package_Transportation_Edit")
                  : t("Origin_Package_Transportation_Create.Title_Origin_Package_Transportation_Create")}
              </Title>
              {data?.type === "EDIT" && (
                <BtnHistory
                  titleRef={originTransportation?.container}
                  object_id={originTransportation?.id}
                  content_type_id={[ContentType.OriginTransportation, ContentType.OriginTransportationImage]}
                />
              )}
            </div>
          </Col>
        </Row>
        <Row align="top" justify="center" style={{ overflowX: "auto" }}>
          <Col span={12} style={{ minWidth: 360 }}>
            <Row gutter={10}>
              <Col flex={6} style={{ minWidth: 320 }}>
                <Form
                  form={formOriginTransportation}
                  labelAlign="left"
                  labelCol={{ flex: "200px" }}
                  initialValues={initialValues}
                >
                  <Row flex={1}>
                    <Col span={24}>
                      <Form.Item
                        label={t("Origin_Package_Transportation_Create.Form.Container")}
                        name="container"
                        rules={[{ required: true }, { validator: validateDataOriginTransportationRequired }]}
                        hasFeedback
                      >
                        <Input
                          placeholder={t("Origin_Package_Transportation_Create.Form.Container")}
                          maxLength={20}
                          style={{ minWidth: 200 }}
                        />
                      </Form.Item>

                      {canViewRestrictedFields && (
                        <Form.Item
                          label={t("Origin_Package_Transportation_Create.Form.External_Container")}
                          name="external_container"
                        >
                          <Input
                            placeholder={t("Origin_Package_Transportation_Create.Form.External_Container")}
                            style={{ minWidth: 200 }}
                          />
                        </Form.Item>
                      )}

                      <Form.Item
                        label={t("Origin_Package_Transportation_Create.Form.Route")}
                        name="route"
                        rules={[{ required: true }, { validator: validateDataOriginTransportationRequired }]}
                      >
                        <Select
                          options={selectOriginPackageRoute}
                          placeholder={t("Please_Select", { ns: "common" })}
                          style={{ minWidth: 200 }}
                        />
                      </Form.Item>

                      <Form.Item
                        label={t("Origin_Package_Transportation_Create.Form.Origin")}
                        name="origin_id"
                        rules={[
                          { required: true, message: "Please select data!" },
                          { validator: validateDataOriginTransportationRequired },
                        ]}
                        validateStatus={selectErrorOrigin ? "error" : ""}
                      >
                        <Select
                          options={listWareHouseOrigin}
                          placeholder={t("Please_Select", { ns: "common" })}
                          onChange={handleSelectOriginChange}
                          style={{ minWidth: 200 }}
                        />
                      </Form.Item>

                      <Form.Item
                        label={t("Origin_Package_Transportation_Create.Form.Destination")}
                        name="destination_id"
                        validateStatus={selectErrorDestination ? "error" : ""}
                      >
                        <Select
                          options={listWareHouseDestination}
                          placeholder={t("Please_Select", { ns: "common" })}
                          onChange={handleSelectDestinationChange}
                          style={{ minWidth: 200 }}
                        />
                      </Form.Item>

                      <Row gutter={24}>
                        <Col span={12}>
                          <Form.Item
                            label={t("Origin_Package_Transportation_Create.Form.Depart_At")}
                            name="depart_at_date"
                            rules={[{ required: true }, { validator: validateDataOriginTransportationRequired }]}
                          >
                            <DatePicker format="YYYY-MM-DD" style={{ minWidth: 200 }} />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="depart_at_time"
                            rules={[{ required: true }, { validator: validateDataOriginTransportationRequired }]}
                          >
                            <TimePicker style={{ minWidth: 200 }} />
                          </Form.Item>
                        </Col>
                      </Row>

                      {canViewRestrictedFields && (
                        <Form.Item
                          label={t("Origin_Package_Transportation_Create.Form.Full_Container_Load")}
                          name="full_container_load"
                          valuePropName="checked"
                        >
                          <Checkbox />
                        </Form.Item>
                      )}

                      {canViewRestrictedFields && (
                        <Form.Item label={t("Origin_Package_Transportation_Create.Form.Note")} name="note">
                          <TextArea rows={4} style={{ minWidth: 200 }} />
                        </Form.Item>
                      )}
                    </Col>
                  </Row>
                  <Row align="middle" justify="end" style={{ marginTop: "20px" }}>
                    {(data?.type === "CREATE" && hasPermission(permissions.OriginPackageTransportation.ADD)) ||
                    (data?.type === "EDIT" && hasPermission(permissions.OriginPackageTransportation.CHANGE)) ? (
                      <Button
                        type="primary"
                        disabled={
                          data?.type === "CREATE"
                            ? validateFieldRequired.fieldContainer &&
                              validateFieldRequired.fieldRoute &&
                              validateFieldRequired.fieldDepartAtDate &&
                              validateFieldRequired.fieldDepartAtTime &&
                              validateFieldRequired.fieldOriginId
                              ? isCheckCreateSuccess
                              : true
                            : false
                        }
                        onClick={onSubmit}
                        style={{ marginRight: "10px" }}
                      >
                        {t("BTN_Save", { ns: "buttons" })}
                      </Button>
                    ) : undefined}
                    <Button type="default" onClick={() => navigation(PACKAGE_LIST_PATH)}>
                      {t("BTN_Cancel", { ns: "buttons" })}
                    </Button>
                  </Row>
                </Form>
              </Col>
              <Col flex={1}></Col>
            </Row>
          </Col>
          <Col span={12}>
            <Form form={formOriginTransportationImage} labelCol={{ flex: "130px" }}>
              <Row gutter={10} style={{ justifyContent: "left", alignSelf: "baseline" }}>
                <Col span={24}>
                  <div style={{ marginBottom: 8 }}>{t("Origin_Package_Transportation_Create.Upload_Title")}</div>
                  <Row gutter={[16, 16]}>
                    {[...Array(8)].map((_, index) => (
                      <Col xs={12} sm={12} md={6} key={index}>
                        <Upload
                          customRequest={handleUpload}
                          listType="picture-card"
                          fileList={fileList[index] ? [fileList[index]] : []}
                          onPreview={handlePreview}
                          disabled={
                            (data?.type === "CREATE" && !hasPermission(permissions.OriginPackageImage.ADD)) ||
                            (data?.type === "EDIT" && !hasPermission(permissions.OriginPackageImage.CHANGE))
                          }
                          onChange={({ fileList: newList }) => handleFileListChange(index, newList)}
                          onRemove={() => handleFileRemove(index)}
                          maxCount={1}
                        >
                          {fileList[index] ? null : uploadButton}
                        </Upload>
                        <div style={{ textAlign: "left" }}>Package {index + 1}</div>
                      </Col>
                    ))}
                  </Row>
                  <ImagePreview
                    previewImage={previewImage}
                    previewOpen={previewOpen}
                    setPreviewOpen={setPreviewOpen}
                    setPreviewImage={setPreviewImage}
                    Image={Image}
                  />
                </Col>
              </Row>
              <Row align="middle" justify="end" style={{ marginTop: "20px" }}>
                {(data?.type === "CREATE" && hasPermission(permissions.OriginPackageTransportation.ADD)) ||
                (data?.type === "EDIT" && hasPermission(permissions.OriginPackageTransportation.CHANGE)) ? (
                  <Button
                    type="primary"
                    disabled={
                      data?.type === "CREATE"
                        ? validateFieldRequired.fieldContainer &&
                          validateFieldRequired.fieldRoute &&
                          validateFieldRequired.fieldDepartAtDate &&
                          validateFieldRequired.fieldDepartAtTime &&
                          validateFieldRequired.fieldOriginId
                          ? isCheckCreateSuccess
                          : true
                        : false
                    }
                    onClick={onSubmitImages}
                    style={{ marginRight: "10px" }}
                  >
                    {t("Button_Origin_Package_Transportation.BTN_Add_Package_Image", {
                      ns: "originpackagetransportation",
                    })}
                  </Button>
                ) : undefined}
              </Row>
            </Form>
          </Col>
        </Row>
        <Row align="middle" justify="center" style={{ marginTop: "20px" }}>
          <Col span={24}>
            <Card>
              <Tabs type="card" items={initialItems} />
            </Card>
          </Col>
        </Row>
      </Spin>
    </>
  );
};
